import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery, useMutation } from '@apollo/client';
import { useTable, useSortBy, usePagination } from 'react-table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { useUserRestrictions } from '../../auth/user-restrictions';
import { usePermissions } from '../../auth';
import { useTeamInfo } from '../../team-id-context';
import { useNotifications } from '../../notifications';
import { GET_CRITICAL_EVENTS_BY_TEAM, MARK_CRITICAL_EVENTS_COMPLETED } from '../../api/queries/criticalEvents';
import { parseLocalDate } from '../../utils/date';
import { ContentHeader } from '../../components/private-layout';
import { ContentBody } from '../../components/private-layout/ContentBody';
import SearchInput from '../../components/search-input';
import { Table } from '../../components/table';
import { Checkbox } from '../../components/checkbox-or-radio-field';
import LoadingSpinner from '../../components/loading-spinner';
import EventDetailsSlideout from '../../components/tracker-table/EventDetailsSlideout';
import NotificationPreferencesSlideout from '../../components/tracker-table/NotificationPreferencesSlideout';
import SubscriptionRequiredModal from '../modals/subscriptionModal/SubscriptionRequiredModal';
import { Dropdown, DropdownToggle, DropdownBody, DropdownItem } from '../../components/dropdown';

import { ReactComponent as CalendarIcon } from '../../shared/icons/calendar.svg';
import { ReactComponent as GearIcon } from '../../shared/icons/gear-icon.svg';
import { ReactComponent as FilterIcon } from '../../shared/icons/filter-icon.svg';

import { formatEventType, getEventUrgency } from '../../components/tracker-table/helpers/eventFormatters';
import { MobileTrackerItem } from './components/MobileTrackerItem';
import { MobilePagination } from '../../components/mobile-layout/MobilePagination';
import TrackerFilterSidebar, { TrackerFilterData } from './components/TrackerFilterSidebar';
import styles from './TrackerPage.module.scss';

dayjs.extend(relativeTime);

interface CriticalEvent {
  id: string;
  eventType: string;
  eventDate: string;
  eventLabel: string;
  status: string;
  document: {
    id: string;
    title: string;
    status: string;
  };
  deal: {
    id: string;
    title: string;
  };
  actorContext: any;
  actionContext: any;
  financialContext: any;
  partyContext: any;
  supportingBlurb: string;
  sourceSection: string;
  sourcePage: number;
  sourceSnippet: string;
  derivationRule: string;
  daysUntilDue?: number | null;
}

const TrackerPage = () => {
  const { formatMessage } = useIntl();
  const { teamId } = useTeamInfo();
  const { canCreateTemplates, restrictionsLoading } = useUserRestrictions();
  const { isAdmin } = usePermissions();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // State
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const [showPreferencesSlideout, setShowPreferencesSlideout] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterEventTypes, setFilterEventTypes] = useState<Set<string>>(new Set());
  const [filterDateRange, setFilterDateRange] = useState<'all' | 'week' | 'month' | 'quarter'>('all');
  const [filterUrgency, setFilterUrgency] = useState<Set<string>>(new Set(['upcoming', 'past_due']));
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState<TrackerFilterData>({
    eventTypes: [],
    dateRange: 'all',
    status: 'all',
    sortBy: 'dueDate',
    sortOrder: 'asc',
  });

  // GraphQL queries and mutations
  const { loading, data, refetch } = useQuery(GET_CRITICAL_EVENTS_BY_TEAM, {
    variables: { teamId },
    skip: !teamId || !canCreateTemplates,
  });

  const [markEventsCompleted, { loading: markingCompleted }] = useMutation(MARK_CRITICAL_EVENTS_COMPLETED, {
    onCompleted: () => {
      notifySuccess({ message: 'Events marked as completed' });
      setSelectedEventIds(new Set());
      setShowBulkActions(false);
      refetch();
    },
    onError: (error) => {
      notifyError({ message: `Failed to mark events as completed: ${error.message}` });
    },
  });

  // Effect to show/hide bulk actions
  useEffect(() => {
    setShowBulkActions(selectedEventIds.size > 0);
  }, [selectedEventIds]);

  // Get unique event types from data
  const availableEventTypes = useMemo(() => {
    if (!data?.criticalEventsByTeam) return [];
    const types = new Set<string>();
    data.criticalEventsByTeam.forEach((event: CriticalEvent) => {
      types.add(event.eventType);
    });
    return Array.from(types).sort();
  }, [data]);

  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All Dates' },
    { value: 'week', label: 'Next 7 Days' },
    { value: 'month', label: 'Next 30 Days' },
    { value: 'quarter', label: 'Next 90 Days' },
  ];

  // Urgency filter options
  const urgencyOptions = [
    { value: 'past_due', label: 'Past Due' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'completed', label: 'Completed' },
  ];

  // Custom relative time formatting - memoized to prevent infinite loops
  const getRelativeTime = useCallback((dateStr: string): string => {
    const eventDate = parseLocalDate(dateStr).startOf('day');
    const now = dayjs().startOf('day');
    const diffDays = eventDate.diff(now, 'days');

    if (diffDays === 0) {
      return 'today';
    } else if (diffDays === 1) {
      return 'in 1d';
    } else if (diffDays > 1) {
      return `in ${diffDays}d`;
    } else if (diffDays === -1) {
      return '1d ago';
    } else {
      return `${Math.abs(diffDays)}d ago`;
    }
  }, []);

  // Filter and prepare data
  const tableData = useMemo(() => {
    if (!data?.criticalEventsByTeam) return [];

    let events = [...data.criticalEventsByTeam] as CriticalEvent[];

    // Apply text filter
    if (filterText) {
      const searchLower = filterText.toLowerCase();
      events = events.filter(
        (event) =>
          event.eventLabel?.toLowerCase().includes(searchLower) ||
          event.document.title?.toLowerCase().includes(searchLower) ||
          event.deal.title?.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filterEventTypes.size > 0) {
      events = events.filter((event) => filterEventTypes.has(event.eventType));
    }

    // Apply date range filter
    const now = dayjs().startOf('day');
    if (filterDateRange !== 'all') {
      let cutoffDate;
      switch (filterDateRange) {
        case 'week':
          cutoffDate = now.add(7, 'days');
          break;
        case 'month':
          cutoffDate = now.add(30, 'days');
          break;
        case 'quarter':
          cutoffDate = now.add(90, 'days');
          break;
      }
      if (cutoffDate) {
        events = events.filter((event) => {
          const eventDate = parseLocalDate(event.eventDate).startOf('day');
          return eventDate.isAfter(now.subtract(1, 'day')) && eventDate.isBefore(cutoffDate.add(1, 'day'));
        });
      }
    }

    // Apply urgency filter (if no filters selected, show all)
    if (filterUrgency.size > 0) {
      events = events.filter((event) => {
        // Check completed status first, before urgency
        if (filterUrgency.has('completed') && event.status === 'completed') {
          return true;
        }
        
        // For non-completed events, check urgency-based filters
        const urgency = getEventUrgency(event.eventDate, event.status);
        const eventDate = parseLocalDate(event.eventDate);
        const now = dayjs();
        const daysUntil = eventDate.diff(now, 'day');
        
        if (filterUrgency.has('past_due') && urgency === 'critical') {
          return true;
        }
        if (filterUrgency.has('upcoming') && (urgency === 'warning' || urgency === 'upcoming' || urgency === 'normal') && event.status !== 'completed') {
          return true;
        }
        // Special handling for 'active_only' - only future events, not today or past
        if (filterUrgency.has('active_only') && daysUntil > 0 && event.status !== 'completed') {
          return true;
        }
        
        return false;
      });
    }

    // Sort by date and move completed to bottom
    events.sort((a, b) => parseLocalDate(a.eventDate).valueOf() - parseLocalDate(b.eventDate).valueOf());
    const activeEvents = events.filter((e) => e.status !== 'completed');
    const completedEvents = events.filter((e) => e.status === 'completed');

    return [...activeEvents, ...completedEvents].map((event) => ({
      ...event,
      selection: event.id,
      dateDisplay: parseLocalDate(event.eventDate).format('MMM D, YYYY'),
      daysAway: getRelativeTime(event.eventDate),
      urgency: getEventUrgency(event.eventDate, event.status),
      onRowClick: () => setActiveEventId(event.id),
    }));
  }, [data, filterText, filterEventTypes, filterDateRange, filterUrgency, setActiveEventId, getRelativeTime]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ data }: any) => (
          <div
            className={styles.checkboxCell}
            onClick={(e) => {
              e.stopPropagation();
              // Toggle select all/none when clicking anywhere in the cell
              const allSelected = data.length > 0 && selectedEventIds.size === data.length;
              if (allSelected) {
                setSelectedEventIds(new Set());
              } else {
                setSelectedEventIds(new Set(data.map((d: any) => d.id)));
              }
            }}
          >
            <Checkbox
              checked={data.length > 0 && selectedEventIds.size === data.length}
              indeterminate={selectedEventIds.size > 0 && selectedEventIds.size < data.length}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                // Backup handler (same logic as wrapper onClick)
                if (e.target.checked) {
                  setSelectedEventIds(new Set(data.map((d: any) => d.id)));
                } else {
                  setSelectedEventIds(new Set());
                }
              }}
            />
          </div>
        ),
        Cell: ({ row }: any) => (
          <div
            className={styles.checkboxCell}
            onClick={(e) => {
              e.stopPropagation();
              // Toggle checkbox when clicking anywhere in the cell
              const newSet = new Set(selectedEventIds);
              if (selectedEventIds.has(row.original.id)) {
                newSet.delete(row.original.id);
              } else {
                newSet.add(row.original.id);
              }
              setSelectedEventIds(newSet);
            }}
          >
            <Checkbox
              checked={selectedEventIds.has(row.original.id)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                const newSet = new Set(selectedEventIds);
                if (e.target.checked) {
                  newSet.add(row.original.id);
                } else {
                  newSet.delete(row.original.id);
                }
                setSelectedEventIds(newSet);
              }}
            />
          </div>
        ),
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        disableSortBy: true,
      },
      {
        Header: <FormattedMessage id="tracker.column.date" defaultMessage="Date" />,
        accessor: 'eventDate',
        Cell: ({ row }: any) => (
          <div className={styles.dateCell}>
            <span className={styles.date}>
              {row.original.dateDisplay} <strong>({row.original.daysAway})</strong>
            </span>
          </div>
        ),
        width: 180,
      },
      {
        Header: <FormattedMessage id="tracker.column.status" defaultMessage="Status" />,
        accessor: 'status',
        Cell: ({ row }: any) => {
          const event = row.original;
          const now = dayjs();
          const eventDate = parseLocalDate(event.eventDate);
          
          // Determine status based on completion and date
          let status, cssClass;
          if (event.status === 'completed') {
            status = 'Completed';
            cssClass = styles.statusPillCompleted;
          } else if (eventDate.isBefore(now, 'day')) {
            status = 'Past Due';
            cssClass = styles.statusPillPast;
          } else {
            status = 'Upcoming';
            cssClass = styles.statusPillUpcoming;
          }
          
          return (
            <span className={`badge badge-pill ${cssClass}`}>
              {status}
            </span>
          );
        },
        width: 100,
      },
      {
        Header: <FormattedMessage id="tracker.column.event" defaultMessage="Event" />,
        accessor: 'eventLabel',
        Cell: ({ value }: any) => (
          <div className={styles.eventCell}>
            <div className={styles.eventLabel}>{value}</div>
          </div>
        ),
      },
      {
        Header: <FormattedMessage id="tracker.column.document" defaultMessage="Document" />,
        accessor: 'document.title',
        Cell: ({ value, row }: any) => (
          <div className={styles.documentCell}>
            <div className={styles.documentTitle}>{value}</div>
            <div className={styles.dealName}>Deal: {row.original.deal.title}</div>
          </div>
        ),
      },
    ],
    [selectedEventIds]
  );

  // React Table instance
  const table = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: [{ id: 'eventDate', desc: false }],
        pageSize: 50,
        pageIndex: 0,
      },
      autoResetPage: false,
    } as any,
    useSortBy,
    usePagination
  );

  // Handlers
  const handleMarkCompleted = useCallback(() => {
    if (selectedEventIds.size === 0) return;
    markEventsCompleted({
      variables: { eventIds: Array.from(selectedEventIds) },
    });
  }, [selectedEventIds, markEventsCompleted]);

  // Loading state
  if (restrictionsLoading || loading) {
    return <LoadingSpinner />;
  }

  // Premium access check
  if (!canCreateTemplates) {
    return (
      <>
        <ContentHeader>
          <div className={styles.header}>
            <div data-testid="tracker-header" className="ml-3">
              <FormattedMessage id="title.tracker" defaultMessage="Critical Date Tracker" />
            </div>
          </div>
        </ContentHeader>
        <ContentBody>
          <div className={styles.noAccess}>
            <CalendarIcon width={48} height={48} />
            <h3>
              <FormattedMessage
                id="tracker.no-access.title"
                defaultMessage="Tracker is a Premium Feature"
              />
            </h3>
            <p>
              <FormattedMessage
                id="tracker.no-access.description"
                defaultMessage="Track critical dates from your executed documents with intelligent reminders and contextual insights."
              />
            </p>
            <Button
              variant="primary"
              onClick={() => setShowSubscriptionModal(true)}
            >
              <FormattedMessage id="cta.upgrade-now" defaultMessage="Upgrade Now" />
            </Button>
          </div>
        </ContentBody>
        <SubscriptionRequiredModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
        />
      </>
    );
  }

  const activeEvent = tableData.find((e) => e.id === activeEventId);

  return (
    <>
      <Helmet title={formatMessage({ id: 'title.tracker', defaultMessage: 'Critical Date Tracker' })} />
      
      <ContentHeader>
        <div className={styles.header}>
          <span data-testid="tracker-header">
            <FormattedMessage id="title.tracker" defaultMessage="Critical Date Tracker" />
          </span>
          {/* Only show settings button on desktop */}
          {isAdmin && (
            <button
              className={`${styles.settingsButton} non-mobile`}
              onClick={() => setShowPreferencesSlideout(true)}
              aria-label="Notification Settings"
            >
              <GearIcon />
            </button>
          )}
        </div>
      </ContentHeader>

      <ContentBody className={styles.contentBodyFull}>
        {/* Mobile Search and Filter Row */}
        <div className="mobile">
          <div className={styles.mobileSearchContainer}>
            <SearchInput
              iconClassName="mobileSearch__icon"
              inputClassName="mobileSearch"
              className={styles.mobileSearch}
              value={filterText}
              onChange={(event: any) => setFilterText(event.currentTarget.value)}
              placeholder={formatMessage({
                id: 'tracker.filter.search.mobile',
                defaultMessage: 'Search events...',
              })}
              aria-label={formatMessage({
                id: 'tracker.filter.search.aria',
                defaultMessage: 'Search critical events',
              })}
            />
            <button
              className="filterBtn"
              onClick={() => setIsMobileFilterOpen(true)}
              aria-label="Open filters"
            >
              <FilterIcon width={20} height={20} />
            </button>
          </div>
        </div>

        {/* Desktop Search and Filter Row */}
        <Row className="mb-3 non-mobile">
          <Col className="mx-3">
            <div className="d-flex align-items-center">
              <SearchInput
                className="mr-3"
                value={filterText}
                onChange={(event: any) => setFilterText(event.currentTarget.value)}
                placeholder={formatMessage({
                  id: 'tracker.filter.search',
                  defaultMessage: 'Search events, documents, or deals',
                })}
                aria-label={formatMessage({
                  id: 'tracker.filter.search.aria',
                  defaultMessage: 'Search critical events',
                })}
              />

              {/* Event Type Filter */}
              <div className="mr-3">
                <Dropdown>
                  <Dropdown.Toggle
                    id="dropdown-filter-event-type"
                    data-testid="dropdown-filter-event-type"
                    as={DropdownToggle}
                    className="w-100"
                  >
                    <FormattedMessage
                      id="tracker.filter.eventType"
                      defaultMessage="Event Type ({count})"
                      values={{
                        count: filterEventTypes.size === 0 ? 'all' : filterEventTypes.size,
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={styles.eventTypeDropdownMenu}>
                    <DropdownItem highlightable>
                      <Checkbox
                        checked={filterEventTypes.size === 0}
                        id="dropdown-filter-event-types-_all"
                        onChange={(event: any) => {
                          const checkbox = event.currentTarget;
                          if (checkbox.checked) {
                            setFilterEventTypes(new Set());
                          } else {
                            setFilterEventTypes(new Set(availableEventTypes));
                          }
                        }}
                        label={<FormattedMessage id="tracker.filter.eventType.all" defaultMessage="All Types" />}
                      />
                    </DropdownItem>
                    <DropdownBody>
                      {availableEventTypes.map((eventType) => (
                        <DropdownItem key={eventType} highlightable>
                          <Checkbox
                            value={eventType}
                            checked={filterEventTypes.has(eventType)}
                            id={`dropdown-filter-event-types-${eventType.toLowerCase().replace(/\s+/g, '-')}`}
                            onChange={(event: any) => {
                              const checkbox = event.currentTarget;
                              const newSet = new Set(filterEventTypes);
                              if (checkbox.checked) newSet.add(checkbox.value);
                              else newSet.delete(checkbox.value);
                              setFilterEventTypes(newSet);
                            }}
                            label={formatEventType(eventType)}
                          />
                        </DropdownItem>
                      ))}
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Date Range Filter */}
              <div className="mr-3">
                <Dropdown>
                  <Dropdown.Toggle
                    id="dropdown-filter-date-range"
                    data-testid="dropdown-filter-date-range"
                    as={DropdownToggle}
                    className="w-100"
                  >
                    <FormattedMessage
                      id="tracker.filter.dateRange"
                      defaultMessage="Date Range: {range}"
                      values={{
                        range: dateRangeOptions.find((opt) => opt.value === filterDateRange)?.label || 'All',
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    <DropdownBody>
                      {dateRangeOptions.map((option) => (
                        <DropdownItem key={option.value} highlightable>
                          <Checkbox
                            type="radio"
                            name="dateRange"
                            value={option.value}
                            checked={filterDateRange === option.value}
                            id={`dropdown-filter-date-range-${option.value}`}
                            onChange={(event: any) => {
                              setFilterDateRange(event.currentTarget.value as any);
                            }}
                            label={option.label}
                          />
                        </DropdownItem>
                      ))}
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Urgency Filter */}
              <div className="mr-3">
                <Dropdown>
                  <Dropdown.Toggle
                    id="dropdown-filter-urgency"
                    data-testid="dropdown-filter-urgency"
                    as={DropdownToggle}
                    className="w-100"
                  >
                    <FormattedMessage
                      id="tracker.filter.urgency"
                      defaultMessage="Status ({count})"
                      values={{
                        count: filterUrgency.size === urgencyOptions.length ? 'all' : filterUrgency.size,
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    <DropdownItem highlightable>
                      <Checkbox
                        checked={filterUrgency.size === urgencyOptions.length}
                        indeterminate={filterUrgency.size > 0 && filterUrgency.size < urgencyOptions.length}
                        id="dropdown-filter-urgency-_all"
                        onChange={(event: any) => {
                          const checkbox = event.currentTarget;
                          if (checkbox.checked) {
                            setFilterUrgency(new Set(urgencyOptions.map((opt) => opt.value)));
                          } else {
                            setFilterUrgency(new Set());
                          }
                        }}
                        label={<FormattedMessage id="tracker.filter.urgency.all" defaultMessage="All Statuses" />}
                      />
                    </DropdownItem>
                    <DropdownBody>
                      {urgencyOptions.map((option) => (
                        <DropdownItem key={option.value} highlightable>
                          <Checkbox
                            value={option.value}
                            checked={filterUrgency.has(option.value)}
                            id={`dropdown-filter-urgency-${option.value}`}
                            onChange={(event: any) => {
                              const checkbox = event.currentTarget;
                              const newSet = new Set(filterUrgency);
                              if (checkbox.checked) newSet.add(checkbox.value);
                              else newSet.delete(checkbox.value);
                              setFilterUrgency(newSet);
                            }}
                            label={option.label}
                          />
                        </DropdownItem>
                      ))}
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Bulk Actions - moved next to Status dropdown */}
              {showBulkActions && (
                <div className={styles.bulkActions}>
                  <span className={styles.selectedCount}>
                    <FormattedMessage
                      id="tracker.selected"
                      defaultMessage="{count} selected"
                      values={{ count: selectedEventIds.size }}
                    />
                  </span>
                  <Button
                    variant="primary"
                    onClick={handleMarkCompleted}
                    disabled={markingCompleted}
                    className="ml-2"
                  >
                    <FormattedMessage id="tracker.mark-completed" defaultMessage="Mark Completed" />
                  </Button>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Mobile Event List */}
        <div className={styles.mobileTableContainer}>
          {tableData.length === 0 ? (
            <div className={styles.emptyState}>
              <CalendarIcon width={48} height={48} />
              <h3>
                <FormattedMessage
                  id="tracker.empty.title"
                  defaultMessage="No Critical Events Found"
                />
              </h3>
              <p>
                <FormattedMessage
                  id="tracker.empty.description"
                  defaultMessage="Critical events will appear here once documents are executed."
                />
              </p>
            </div>
          ) : (
            <>
              <div className={styles.mobileEventList}>
                {(table as any).page?.map((row: any) => {
                  const event = row.original;
                  const eventDate = parseLocalDate(event.eventDate);
                  const now = dayjs();

                  // Use EXACT same status logic as desktop
                  let displayStatus: string;
                  let statusClass: string;
                  if (event.status === 'completed') {
                    displayStatus = 'Completed';
                    statusClass = 'completed';
                  } else if (eventDate.isBefore(now, 'day')) {
                    displayStatus = 'Past Due';
                    statusClass = 'past_due';
                  } else {
                    displayStatus = 'Upcoming';
                    statusClass = 'upcoming';
                  }

                  return (
                    <MobileTrackerItem
                      key={event.id}
                      event={{
                        id: event.id,
                        documentName: event.document.title,
                        dealName: event.deal.title,
                        eventType: event.eventType,
                        // Pass the exact display status and class
                        displayStatus: displayStatus,
                        statusClass: statusClass,
                        criticalDate: event.eventDate,
                        // Pass the same formatted data as desktop
                        dateDisplay: event.dateDisplay,
                        daysAway: event.daysAway,
                      }}
                      onClick={() => setActiveEventId(event.id)}
                    />
                  );
                })}
              </div>
              <MobilePagination
                pageIndex={(table as any).state.pageIndex}
                pageCount={(table as any).pageCount}
                canPreviousPage={(table as any).canPreviousPage}
                canNextPage={(table as any).canNextPage}
                gotoPage={(table as any).gotoPage}
                previousPage={(table as any).previousPage}
                nextPage={(table as any).nextPage}
                totalItems={tableData.length}
                pageSize={(table as any).state.pageSize}
              />
            </>
          )}
        </div>

        {/* Desktop Table */}
        <div className={`${styles.scrollableContainer} non-mobile`}>
          <Row>
            <Col>
              {tableData.length === 0 ? (
                <div className={styles.emptyState}>
                  <CalendarIcon width={48} height={48} />
                  <h3>
                    <FormattedMessage
                      id="tracker.empty.title"
                      defaultMessage="No Critical Events Found"
                    />
                  </h3>
                  <p>
                    <FormattedMessage
                      id="tracker.empty.description"
                      defaultMessage="Critical events will appear here once documents are executed."
                    />
                  </p>
                </div>
              ) : (
                <div className={styles.tableWrapper}>
                  <Table
                    table={table}
                    striped
                    isStickyHeader
                    paginationBelow
                    isStickyPagination
                  />
                </div>
              )}
            </Col>
          </Row>
        </div>
      </ContentBody>

      {/* Event Details Slideout - Always rendered for smooth animations */}
      <EventDetailsSlideout
        event={activeEvent || tableData[0] || {
          id: '',
          eventType: '',
          eventDate: new Date().toISOString(),
          eventLabel: '',
          status: '',
          document: { id: '', title: '', status: '' },
          deal: { id: '', title: '' },
          actorContext: null,
          actionContext: null,
          financialContext: null,
          partyContext: null,
          supportingBlurb: '',
          sourceSection: '',
          sourcePage: 0,
          sourceSnippet: '',
          derivationRule: '',
        }}
        isOpen={!!activeEventId && !!activeEvent}
        onClose={() => setActiveEventId(null)}
        onNavigateToEvent={(eventId) => {
          const event = tableData.find((e) => e.id === eventId);
          if (event) setActiveEventId(eventId);
        }}
        teamId={teamId}
      />

      {/* Notification Preferences Slideout */}
      <NotificationPreferencesSlideout
        isOpen={showPreferencesSlideout}
        onClose={() => setShowPreferencesSlideout(false)}
        teamId={teamId}
      />

      {/* Mobile Filter Sidebar */}
      <TrackerFilterSidebar
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        filters={mobileFilters}
        onApplyFilters={(filters) => {
          setMobileFilters(filters);
          // Apply filters to the main filter states
          setFilterEventTypes(new Set(filters.eventTypes));
          setFilterDateRange(filters.dateRange as any);
          
          // Handle status filter - Note: 'active' means truly upcoming, not due today or past due
          if (filters.status === 'all') {
            // Clear urgency filter to show all
            setFilterUrgency(new Set());
          } else if (filters.status === 'completed') {
            setFilterUrgency(new Set(['completed']));
          } else if (filters.status === 'past_due') {
            setFilterUrgency(new Set(['past_due']));
          } else if (filters.status === 'active') {
            // For 'active', we want only future events (not due today, not past due)
            setFilterUrgency(new Set(['active_only']));
          }
        }}
      />
    </>
  );
};

export default TrackerPage;